import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { TrendingUp, Euro, Calendar, PieChart as PieChartIcon, BarChart3 } from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

const Statistiken = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to ensure we always have an array
  const toArray = (data) => Array.isArray(data) ? data : [];

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/financial-stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Statistiken");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatMonthYear = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' });
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Lädt Statistiken...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="statistiken-container">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Finanz-Statistiken</h1>
        <p className="text-sm sm:text-base text-gray-600">Übersicht über Einnahmen und Zahlungen</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="card-white card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Gesamteinnahmen</CardTitle>
            <Euro className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-green-600">{formatCurrency(stats.total_income)}</div>
            <p className="text-xs text-gray-600 mt-1">{stats.total_payments} Zahlungen</p>
          </CardContent>
        </Card>

        <Card className="card-white card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Ø Monatlich</CardTitle>
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(stats.average_monthly)}</div>
            <p className="text-xs text-gray-600 mt-1">Durchschnitt</p>
          </CardContent>
        </Card>

        <Card className="card-white card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Letzter Monat</CardTitle>
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">
              {toArray(stats.monthly_income).length > 0 
                ? formatCurrency(toArray(stats.monthly_income)[toArray(stats.monthly_income).length - 1]?.betrag || 0)
                : formatCurrency(0)
              }
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {toArray(stats.monthly_income).length > 0 
                ? formatMonthYear(toArray(stats.monthly_income)[toArray(stats.monthly_income).length - 1]?.month || '')
                : '-'
              }
            </p>
          </CardContent>
        </Card>

        <Card className="card-white card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Wachen</CardTitle>
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{toArray(stats.income_by_wache).length}</div>
            <p className="text-xs text-gray-600 mt-1">Aktive Wachen</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Income Chart */}
      <Card className="card-white">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Monatliche Einnahmen (Letzte 12 Monate)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] sm:h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={toArray(stats.monthly_income)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="month" 
                  tickFormatter={formatMonthYear}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickFormatter={(value) => `${value}€`}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={formatMonthYear}
                  contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                />
                <Bar dataKey="betrag" fill="#3B82F6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Yearly Income Chart */}
        <Card className="card-white">
          <CardHeader>
            <CardTitle className="text-base sm:text-lg text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Jährliche Einnahmen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={toArray(stats.yearly_income)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="year" 
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                  />
                  <YAxis 
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                    tickFormatter={(value) => `${value}€`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="betrag" 
                    stroke="#10B981" 
                    strokeWidth={3}
                    dot={{ fill: '#10B981', r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Income by Wache Chart */}
        <Card className="card-white">
          <CardHeader>
            <CardTitle className="text-base sm:text-lg text-gray-900 flex items-center gap-2">
              <PieChartIcon className="w-5 h-5" />
              Einnahmen nach Wachen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={toArray(stats.income_by_wache)}
                    dataKey="betrag"
                    nameKey="wache"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={(entry) => `${entry.wache}: ${formatCurrency(entry.betrag)}`}
                    labelLine={false}
                  >
                    {toArray(stats.income_by_wache).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Income by Wache Table */}
      <Card className="card-white">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg text-gray-900">Detaillierte Aufschlüsselung nach Wachen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Wache</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Einnahmen</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Anteil</th>
                </tr>
              </thead>
              <tbody>
                {toArray(stats.income_by_wache).map((item, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        {item.wache}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm font-semibold text-right text-gray-900">
                      {formatCurrency(item.betrag)}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600">
                      {(((item.betrag || 0) / (stats.total_income || 1)) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 font-bold">
                  <td className="py-3 px-4 text-sm text-gray-900">Gesamt</td>
                  <td className="py-3 px-4 text-sm text-right text-gray-900">
                    {formatCurrency(stats.total_income)}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-900">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Statistiken;
